/**
 * ThemeGallery Component
 * Display uploaded themes and their layouts in a grid
 */

import React, { useState } from 'react';
import { Trash2, RefreshCw, ChevronDown, ChevronRight, Image, Palette, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '@/lib/utils';

export interface Layout {
  templateId: string;
  layoutId: string;
  layoutName: string;
  gcsPath: string;
  previewPath: string;
  previewUrl?: string;
  description: string | null;
  createdAt: string;
}

export interface Theme {
  id: string;
  name: string;
  category: string;
  layouts: Layout[];
}

interface ThemeGalleryProps {
  themes: Theme[];
  onDelete: (themeId: string) => void;
  onRegeneratePreviews: (themeId: string) => void;
  onRefresh: () => void;
}

export const ThemeGallery: React.FC<ThemeGalleryProps> = ({
  themes,
  onDelete,
  onRegeneratePreviews,
  onRefresh: _onRefresh,
}) => {
  const [expandedThemes, setExpandedThemes] = useState<Set<string>>(new Set());

  const toggleTheme = (themeId: string) => {
    setExpandedThemes(prev => {
      const next = new Set(prev);
      if (next.has(themeId)) {
        next.delete(themeId);
      } else {
        next.add(themeId);
      }
      return next;
    });
  };

  if (themes.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Palette className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Themes Uploaded</h3>
          <p className="text-sm text-muted-foreground text-center max-w-md">
            Upload your first theme by dragging a theme folder onto the upload area above.
            Each theme should contain layout folders with slide.json files.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Uploaded Themes ({themes.length})</h2>

      {themes.map((theme) => {
        const isExpanded = expandedThemes.has(theme.id);

        return (
          <Card key={theme.id} className="overflow-hidden">
            <CardHeader
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => toggleTheme(theme.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <CardTitle className="text-lg">{theme.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {theme.layouts.length} layout{theme.layouts.length !== 1 ? 's' : ''} • {theme.category}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRegeneratePreviews(theme.id)}
                    title="Regenerate preview images"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Regenerate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(theme.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className="pt-0 border-t">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-4">
                  {theme.layouts.map((layout) => (
                    <LayoutCard key={layout.templateId} layout={layout} />
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};

interface LayoutCardProps {
  layout: Layout;
}

const LayoutCard: React.FC<LayoutCardProps> = ({ layout }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  return (
    <div className="group relative bg-muted rounded-lg overflow-hidden border">
      {/* Preview Image */}
      <div className="aspect-video relative bg-muted">
        {layout.previewUrl && !imageError ? (
          <>
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-pulse bg-muted-foreground/10 w-full h-full" />
              </div>
            )}
            <img
              src={layout.previewUrl}
              alt={layout.layoutName}
              className={cn(
                "w-full h-full object-cover transition-opacity",
                imageLoading ? "opacity-0" : "opacity-100"
              )}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageError(true);
                setImageLoading(false);
              }}
            />
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
            <Image className="h-8 w-8 mb-2" />
            <span className="text-xs">No preview</span>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <a
            href={layout.gcsPath}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white text-xs flex items-center gap-1 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3 w-3" />
            View in GCS
          </a>
        </div>
      </div>

      {/* Info */}
      <div className="p-2">
        <p className="text-sm font-medium truncate" title={layout.layoutName}>
          {layout.layoutId}
        </p>
        {layout.description && (
          <p className="text-xs text-muted-foreground truncate" title={layout.description}>
            {layout.description}
          </p>
        )}
      </div>
    </div>
  );
};
